/**
 * reports-timezone-select: the Reports scope stores CALENDAR day bounds as
 * UTC midnights (all existing presets/coverage/period math). When a timezone
 * is chosen, the EFFECTIVE request window shifts each calendar day boundary
 * to that day's midnight in the zone — core dashboard numbers are filtered by
 * exact `event_timestamp` bounds server-side, so they become true local days.
 * Daily trend series stay UTC-bucketed (materialized) — callers must say so.
 */

const partsCache = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let formatter = partsCache.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    partsCache.set(timeZone, formatter);
  }
  return formatter;
}

/** Zone offset (ms east of UTC) at the given instant. */
export function zoneOffsetMs(utcMs: number, timeZone: string): number {
  const parts = formatterFor(timeZone).formatToParts(new Date(utcMs));
  const value: Record<string, number> = {};
  for (const part of parts) {
    if (part.type !== 'literal') value[part.type] = Number(part.value);
  }
  const asUtc = Date.UTC(
    value.year,
    (value.month ?? 1) - 1,
    value.day ?? 1,
    value.hour === 24 ? 0 : value.hour ?? 0,
    value.minute ?? 0,
    value.second ?? 0,
  );
  return asUtc - utcMs;
}

export function isValidTimeZone(timeZone: string): boolean {
  if (!timeZone) return false;
  try {
    formatterFor(timeZone);
    return true;
  } catch {
    return false;
  }
}

/**
 * The instant of `calendar day of utcMidnightIso`'s midnight in the zone.
 * Input MUST be a UTC midnight (the canonical scope day bound); anything else
 * (or an invalid zone) returns the input unchanged — never a shifted lie.
 */
export function zonedDayBoundIso(utcMidnightIso: string, timeZone: string): string {
  if (!timeZone || timeZone === 'UTC') return utcMidnightIso;
  const utcMs = Date.parse(utcMidnightIso);
  if (!Number.isFinite(utcMs) || utcMs % 86_400_000 !== 0 || !isValidTimeZone(timeZone)) {
    return utcMidnightIso;
  }
  // First guess: same instant shifted by the offset AT the UTC midnight; one
  // refinement handles DST transitions crossing the boundary.
  let candidate = utcMs - zoneOffsetMs(utcMs, timeZone);
  candidate = utcMs - zoneOffsetMs(candidate, timeZone);
  return new Date(candidate).toISOString();
}

/** «UTC+02:00» style label for the zone at the given instant (now-ish). */
export function zoneOffsetLabel(timeZone: string, atMs = 1_750_000_000_000): string {
  if (timeZone === 'UTC') return 'UTC±00:00';
  if (!isValidTimeZone(timeZone)) return '';
  const offsetMinutes = Math.round(zoneOffsetMs(atMs, timeZone) / 60_000);
  const sign = offsetMinutes < 0 ? '−' : '+';
  const absolute = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absolute / 60)).padStart(2, '0');
  const minutes = String(absolute % 60).padStart(2, '0');
  return `UTC${sign}${hours}:${minutes}`;
}

/** Options: UTC first, browser zone second (marked), then the IANA list. */
export function buildTimeZoneOptions(): string[] {
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const supported: string[] = typeof Intl.supportedValuesOf === 'function'
    ? Intl.supportedValuesOf('timeZone')
    : [];
  const head = ['UTC', ...(browserZone && browserZone !== 'UTC' ? [browserZone] : [])];
  return [...head, ...supported.filter(zone => !head.includes(zone))];
}

export function getBrowserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}
